import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Response, Attachment } from '@/api/types';
import api from '@/api';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';

interface ResponseDraftEditorProps {
  response: Response;
  canEdit: boolean;
  onSave: (data: { mailSubject: string; mailBody: string; attachments?: string[] }) => void;
  onCancel?: () => void;
  isSaving?: boolean;
  workflow?: {
    name: string;
    stages?: Array<{ stageName: string }>;
  };
}

export const ResponseDraftEditor: React.FC<ResponseDraftEditorProps> = ({
  response,
  canEdit,
  onSave,
  onCancel,
  isSaving = false,
  workflow
}) => {

  const { addNotification } = useAppStore();
  const [isEditing, setIsEditing] = useState(false);
  const [subject, setSubject] = useState(response.mailSubject || '');
  const [body, setBody] = useState(response.mailBody || '');
  const [attachmentIds, setAttachmentIds] = useState<string[]>(response.attachments || []);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showOcrModal, setShowOcrModal] = useState(false);
  const [selectedAttachmentForOcr, setSelectedAttachmentForOcr] = useState<Attachment | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedAttachmentForPreview, setSelectedAttachmentForPreview] = useState<Attachment | null>(null);
  const [previewContent, setPreviewContent] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [requestingRelease, setRequestingRelease] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releaseReason, setReleaseReason] = useState('');
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [selectedAttachmentForReplace, setSelectedAttachmentForReplace] = useState<Attachment | null>(null);
  const [replacementFile, setReplacementFile] = useState<File | null>(null);
  const [replaceComment, setReplaceComment] = useState('');
  const [replaceDisplayName, setReplaceDisplayName] = useState('');
  const [replaceDescription, setReplaceDescription] = useState('');
  const [replacingAttachment, setReplacingAttachment] = useState(false);
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
  const [selectedAttachmentForHistory, setSelectedAttachmentForHistory] = useState<Attachment | null>(null);
  const [versionHistory, setVersionHistory] = useState<any>(null);
  const [loadingVersionHistory, setLoadingVersionHistory] = useState(false);
  const [showDraftHistory, setShowDraftHistory] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceFileInputRef = useRef<HTMLInputElement>(null);
  const { user: currentUser} = useAuthStore();

  // Check if current user already has a pending release request
  const currentUserHasPendingRequest = () => {
    if (!currentUser || !response.draftLock?.releaseRequests) return false;

    return response.draftLock.releaseRequests.some((request: any) => {
      if (request.status !== 'pending') return false;

      // Check if userId matches current user
      if (typeof request.userId === 'string') {
        return request.userId === currentUser._id;
      } else {
        return request.userId._id === currentUser._id;
      }
    });
  };

  useEffect(() => {
    setSubject(response.mailSubject || '');
    setBody(response.mailBody || '');

    // Normalize attachments — handle both formats:
    // 1. Flat populated objects: [{ _id, filename, ... }]
    // 2. Flat IDs: ["id1", "id2"]
    const attachmentData = response.attachments || [];
    if (attachmentData.length > 0) {
      const first = attachmentData[0] as any;
      if (typeof first === 'object' && first?._id) {
        // Populated objects
        const populatedAttachments = attachmentData as unknown as Attachment[];
        setAttachmentIds(populatedAttachments.map(att => att._id).filter(Boolean) as string[]);
        setAttachments(populatedAttachments);
      } else {
        // Flat IDs (strings)
        setAttachmentIds(attachmentData.map((att: any) =>
          typeof att === 'string' ? att : (att?._id || att)
        ).filter(Boolean) as string[]);
      }
    } else {
      setAttachmentIds([]);
      setAttachments([]);
    }
  }, [response.mailSubject, response.mailBody, response.attachments]);

  useEffect(() => {
    const changed =
      subject !== (response.mailSubject || '') ||
      body !== (response.mailBody || '') ||
      JSON.stringify(attachmentIds) !== JSON.stringify(response.attachments || []);
    setHasChanges(changed);
  }, [subject, body, attachmentIds, response.mailSubject, response.mailBody, response.attachments]);

  // Fetch full attachment details when attachmentIds change
  useEffect(() => {

    const fetchAttachments = async () => {
      if (attachmentIds.length === 0) {
        setAttachments([]);
        return;
      }

      // Check if attachmentIds contains objects instead of strings
      // This can happen during initial render before first useEffect runs
      if (attachmentIds.length > 0 && typeof attachmentIds[0] === 'object' && (attachmentIds[0] as any)?._id) {
        const populatedAttachments = attachmentIds as unknown as Attachment[];
        setAttachments(populatedAttachments);
        // Update attachmentIds to be just the string IDs
        setAttachmentIds(populatedAttachments.map(att => att._id).filter(Boolean) as string[]);
        return;
      }

      setLoadingAttachments(true);
      try {
        // Fetch attachments individually and handle errors gracefully
        const fetchedAttachments = [];
        for (const id of attachmentIds) {
          try {
            // The API client unwraps StandardResponse automatically, returning just the Attachment
            const attachment = await api.attachments.get(id as string) as any as Attachment;

            if (attachment && attachment._id) {
              fetchedAttachments.push(attachment);
            } else {
              console.warn('📎 API returned but no attachment data:', attachment);
            }
          } catch (error) {
            // If individual attachment fetch fails, create a placeholder
            console.warn(`Failed to fetch attachment ${id}, using placeholder:`, error);
            fetchedAttachments.push({
              _id: id as string,
              id: id as string,
              filename: 'Attachment (loading failed)',
              size: 0,
              contentType: 'application/octet-stream',
              s3Url: null,
              attachmentType: 'response',
              storageTier: 'permanent',
              organization: '',
              userId: '',
              isPublic: false,
              virusScanStatus: 'pending',
              processingStatus: 'pending',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            } as any);
          }
        }
        setAttachments(fetchedAttachments);
      } catch (error) {
        console.error('Failed to fetch attachments:', error);
        setAttachments([]);
      } finally {
        setLoadingAttachments(false);
      }
    };

    fetchAttachments();
  }, [attachmentIds]);

  const handleSave = () => {
    onSave({ mailSubject: subject, mailBody: body, attachments: attachmentIds });
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleCancel = () => {
    setSubject(response.mailSubject || '');
    setBody(response.mailBody || '');
    setAttachmentIds(response.attachments || []);
    setIsEditing(false);
    setHasChanges(false);
    onCancel?.();
  };

  const handleEdit = () => {
    if (canEdit) {
      setIsEditing(true);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingFiles(true);
    try {
      const uploadedAttachments = await api.attachments.upload({
        files: Array.from(files),
        attachmentType: 'response',
        responseId: response._id || response.id
      });

      if (uploadedAttachments.success && uploadedAttachments.data?.attachments) {
        const newAttachmentIds = uploadedAttachments.data.attachments
          .filter(result => result.success && result.attachment)
          .map(result => result.attachment!._id || result.attachment!.id)
          .filter(Boolean) as string[];
        setAttachmentIds(prev => [...prev, ...newAttachmentIds]);
      }
    } catch (error: any) {
      console.error('Failed to upload attachments:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error.message || 'Failed to upload files';
      console.error('Server error details:', error?.response?.data);
      addNotification({ type: 'error', title: 'Upload Failed', message: errorMessage });
    } finally {
      setUploadingFiles(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    setAttachmentIds(prev => prev.filter(id => id !== attachmentId));
  };

  const handleAddAttachmentsClick = () => {
    fileInputRef.current?.click();
  };

  const handleRequestRelease = async () => {
    if (!response._id && !response.id) return;

    setRequestingRelease(true);
    try {
      await api.responses.requestRelease(response._id || response.id!, releaseReason);
      setShowReleaseModal(false);
      setReleaseReason('');
      addNotification({ type: 'success', title: 'Release Requested', message: 'The lock owner will be notified.' });
      if (onCancel) onCancel();
    } catch (error: any) {
      addNotification({ type: 'error', title: 'Request Failed', message: error?.response?.data?.message || error.message });
    } finally {
      setRequestingRelease(false);
    }
  };

  const getLockedByName = () => {
    if (!response.draftLock?.lockedBy) return 'Unknown User';

    const lockedBy = response.draftLock.lockedBy;
    if (typeof lockedBy === 'string') return lockedBy;

    if (lockedBy.firstName && lockedBy.lastName) {
      return `${lockedBy.firstName} ${lockedBy.lastName}`;
    }
    return lockedBy.username || lockedBy.email || 'Unknown User';
  };

  const formatLockExpiry = () => {
    if (!response.draftLock?.lockExpiry) return '';

    const expiry = new Date(response.draftLock.lockExpiry);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} min`;
    }
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  // Helper to extract presigned URL from response (handleResponse unwraps body.data)
  const extractPresignedUrl = (resp: any): string | undefined => {
    return resp?.presignedUrl || resp?.data?.presignedUrl;
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      // Use presigned URL for download (works without auth headers)
      if (!attachment._id) {
        throw new Error('Attachment ID is missing');
      }
      const presignedResponse = await api.attachments.getPresignedUrl(attachment._id) as any;
      // handleResponse unwraps body.data, so presignedUrl is at top level
      const presignedUrl = presignedResponse?.presignedUrl || presignedResponse?.data?.presignedUrl;
      if (!presignedUrl) {
        throw new Error('Failed to get presigned URL');
      }
      // Open presigned URL in new tab to trigger download
      window.open(presignedUrl, '_blank');
    } catch (error) {
      console.error('Failed to download attachment:', error);
      addNotification({ type: 'error', title: 'Download Failed', message: 'Failed to download attachment. Please try again.' });
    }
  };

  const handleViewOcr = (attachment: Attachment) => {
    setSelectedAttachmentForOcr(attachment);
    setShowOcrModal(true);
  };

  const handlePreviewAttachment = async (attachment: Attachment) => {
    setSelectedAttachmentForPreview(attachment);
    setShowPreviewModal(true);
    setLoadingPreview(true);
    setPreviewContent(null);

    try {
      const fileType = attachment.contentType?.toLowerCase() || '';
      const fileName = attachment.filename?.toLowerCase() || '';

      // For CSV and XLSX files, fetch and parse the content
      if (fileType.includes('csv') || fileName.endsWith('.csv')) {
        if (!attachment._id) {
          throw new Error('Attachment ID is missing');
        }
        const presignedResponse = await api.attachments.getPresignedUrl(attachment._id);
        if (!extractPresignedUrl(presignedResponse)) {
          throw new Error('Failed to get presigned URL');
        }
        const response = await fetch(extractPresignedUrl(presignedResponse)!);
        const text = await response.text();
        const rows = text.split('\n').map(row => row.split(','));
        setPreviewContent({ type: 'csv', data: rows });
      } else if (
        fileType.includes('spreadsheet') ||
        fileType.includes('excel') ||
        fileName.endsWith('.xlsx') ||
        fileName.endsWith('.xls')
      ) {
        // For Excel files, we'll show a message to download since we need a library to parse
        setPreviewContent({
          type: 'excel',
          message: 'Excel file preview requires downloading. Click the download button to view the full file.',
          url: attachment.s3Url
        });
      } else if (fileType.includes('pdf') || fileName.endsWith('.pdf')) {
        // Use presigned URL for inline viewing (works in iframes without auth headers)
        if (!attachment._id) {
          throw new Error('Attachment ID is missing');
        }
        const presignedResponse = await api.attachments.getPresignedUrl(attachment._id);
        if (!extractPresignedUrl(presignedResponse)) {
          throw new Error('Failed to get presigned URL');
        }
        setPreviewContent({ type: 'pdf', url: extractPresignedUrl(presignedResponse)! });
      } else if (fileType.includes('image')) {
        // Use presigned URL for inline viewing (works in iframes without auth headers)
        if (!attachment._id) {
          throw new Error('Attachment ID is missing');
        }
        const presignedResponse = await api.attachments.getPresignedUrl(attachment._id);
        if (!extractPresignedUrl(presignedResponse)) {
          throw new Error('Failed to get presigned URL');
        }
        setPreviewContent({ type: 'image', url: extractPresignedUrl(presignedResponse)! });
      } else if (fileType.includes('text') || fileName.endsWith('.txt')) {
        if (!attachment._id) {
          throw new Error('Attachment ID is missing');
        }
        const presignedResponse = await api.attachments.getPresignedUrl(attachment._id);
        if (!extractPresignedUrl(presignedResponse)) {
          throw new Error('Failed to get presigned URL');
        }
        const response = await fetch(extractPresignedUrl(presignedResponse)!);
        const text = await response.text();
        setPreviewContent({ type: 'text', data: text });
      } else {
        setPreviewContent({
          type: 'unsupported',
          message: 'Preview not available for this file type. Click download to view the file.',
          url: attachment.s3Url
        });
      }
    } catch (error) {
      console.error('Failed to load preview:', error);
      setPreviewContent({
        type: 'error',
        message: 'Failed to load preview. Click download to view the file.',
        url: attachment.s3Url
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const canPreview = (attachment: Attachment) => {
    const fileType = attachment.contentType?.toLowerCase() || '';
    const fileName = attachment.filename?.toLowerCase() || '';

    return (
      fileType.includes('csv') ||
      fileType.includes('spreadsheet') ||
      fileType.includes('excel') ||
      fileType.includes('pdf') ||
      fileType.includes('image') ||
      fileType.includes('text') ||
      fileName.endsWith('.csv') ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.xls') ||
      fileName.endsWith('.pdf') ||
      fileName.endsWith('.txt') ||
      fileName.endsWith('.png') ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.gif')
    );
  };

  const handleReplaceAttachment = (attachment: Attachment) => {
    setSelectedAttachmentForReplace(attachment);
    setShowReplaceModal(true);
    setReplacementFile(null);
    setReplaceComment('');
    setReplaceDisplayName('');
    setReplaceDescription('');
  };

  const handleCloseReplaceModal = () => {
    setShowReplaceModal(false);
    setSelectedAttachmentForReplace(null);
    setReplacementFile(null);
    setReplaceComment('');
    setReplaceDisplayName('');
    setReplaceDescription('');
  };

  const handleReplaceFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setReplacementFile(file);
    }
  };

  const handleSubmitReplacement = async () => {
    if (!selectedAttachmentForReplace || !replacementFile) {
      return;
    }

    setReplacingAttachment(true);
    try {
      await api.attachments.replaceAttachment(
        selectedAttachmentForReplace._id!,
        replacementFile,
        {
          changeComment: replaceComment || undefined,
          displayName: replaceDisplayName || undefined,
          description: replaceDescription || undefined
        }
      );

      // Refresh attachment list
      const updatedAttachment = await api.attachments.get(selectedAttachmentForReplace._id!);
      setAttachments(prev =>
        prev.map(att => att._id === selectedAttachmentForReplace._id ? (updatedAttachment.data || att) : att)
      );

      handleCloseReplaceModal();
      addNotification({ type: 'success', title: 'Replaced', message: 'Attachment replaced successfully.' });
    } catch (error) {
      console.error('Failed to replace attachment:', error);
      addNotification({ type: 'error', title: 'Replace Failed', message: 'Failed to replace attachment. Please try again.' });
    } finally {
      setReplacingAttachment(false);
    }
  };

  const handleViewVersionHistory = async (attachment: Attachment) => {
    if (!attachment._id) return;

    setSelectedAttachmentForHistory(attachment);
    setShowVersionHistoryModal(true);
    setLoadingVersionHistory(true);

    try {
      const history = await api.attachments.getVersionHistory(attachment._id);
      setVersionHistory(history.data);
    } catch (error) {
      console.error('Failed to load version history:', error);
      addNotification({ type: 'error', title: 'Load Failed', message: 'Failed to load version history.' });
    } finally {
      setLoadingVersionHistory(false);
    }
  };

  const handleDownloadVersion = async (attachmentId: string, versionNumber: number) => {
    try {
      await api.attachments.downloadVersion(attachmentId, versionNumber);
    } catch (error) {
      console.error('Failed to download version:', error);
      addNotification({ type: 'error', title: 'Download Failed', message: 'Failed to download version.' });
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Get next stage name
  const getNextStageName = (): string | null => {
    if (!workflow?.stages || response.status.toString() !== 'Draft') return null;
    const firstNonDraftStage = workflow.stages.find(s => s.stageName !== 'Draft') || workflow.stages[0];
    return firstNonDraftStage?.stageName || null;
  };

  const nextStageName = getNextStageName();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Response Draft
            </h2>
            {nextStageName && response.status.toString() === 'Draft' && (
              <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                Next: {nextStageName}
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isEditing ? 'Editing draft' : 'View mode'}
            {hasChanges && <span className="ml-2 text-amber-600">• Unsaved changes</span>}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          {!isEditing && canEdit && (
            <Button onClick={handleEdit} variant="primary" size="md" className="shadow-sm">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Edit Draft
            </Button>
          )}

          {isEditing && (
            <>
              <Button
                onClick={handleCancel}
                variant="ghost"
                size="sm"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="primary"
                size="sm"
                disabled={!hasChanges || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Draft'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Draft Lock Banner */}
      {response.draftLock?.isLocked && !canEdit && (
        <div className="mx-4 mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                  Draft Locked
                </h3>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                  This draft is currently being edited by <span className="font-medium">{getLockedByName()}</span>
                  {response.draftLock.lockExpiry && (
                    <span className="ml-1">
                      • Lock expires in {formatLockExpiry()}
                    </span>
                  )}
                </p>
                {response.draftLock.lockedAt && (
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    Locked since {new Date(response.draftLock.lockedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            {!currentUserHasPendingRequest() ? (
              <Button
                onClick={() => setShowReleaseModal(true)}
                variant="secondary"
                size="sm"
                className="flex-shrink-0 bg-white dark:bg-gray-700 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-gray-600"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
                Request Release
              </Button>
            ) : (
              <span className="flex-shrink-0 px-3 py-2 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded border border-blue-200 dark:border-blue-800">
                Request Pending
              </span>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Subject
          </label>
          {isEditing ? (
            <Input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
              className="w-full"
              disabled={isSaving}
            />
          ) : (
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-sm text-gray-900 dark:text-gray-100">
              {subject || (
                <span className="text-gray-400 dark:text-gray-500 italic">
                  No subject
                </span>
              )}
            </div>
          )}
        </div>

        {/* Body */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Message Body
          </label>
          {isEditing ? (
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Enter email body"
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-y min-h-[200px]"
              disabled={isSaving}
            />
          ) : (
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap min-h-[200px] max-h-[400px] overflow-y-auto">
              {body || (
                <span className="text-gray-400 dark:text-gray-500 italic">
                  No message body
                </span>
              )}
            </div>
          )}
        </div>

        {/* Attachments Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Attachments
          </label>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={!isEditing}
          />

          {/* Closed-response split: signed letter shown by default, working drafts behind a toggle */}
          {(() => {
            const closureSet = new Set<string>(
              (((response as any).closureDetails?.signedLetterAttachmentIds) || [])
                .map((x: any) => (x && typeof x === 'object' ? (x._id || x.id) : x))
                .map((s: any) => String(s))
            );
            const isClosedWithLetter = (response.status === 'Closed' || response.status === 'Sent') && closureSet.size > 0;
            return isClosedWithLetter ? (
              <div className="mb-2 -mt-1 flex items-center justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">
                  Showing signed letter only. Working drafts moved to version history.
                </span>
                <button
                  type="button"
                  onClick={() => setShowDraftHistory(v => !v)}
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
                >
                  {showDraftHistory ? 'Hide working drafts' : 'Show working drafts'}
                </button>
              </div>
            ) : null;
          })()}

          {/* Existing attachments list */}
          {loadingAttachments ? (
            <div className="mb-3 p-4 text-center text-gray-500 dark:text-gray-400">
              Loading attachments...
            </div>
          ) : attachments.length > 0 ? (
            <div className="mb-3 space-y-3">
              {attachments.filter(att => {
                const closureSet = new Set<string>(
                  (((response as any).closureDetails?.signedLetterAttachmentIds) || [])
                    .map((x: any) => (x && typeof x === 'object' ? (x._id || x.id) : x))
                    .map((s: any) => String(s))
                );
                const isClosedWithLetter = (response.status === 'Closed' || response.status === 'Sent') && closureSet.size > 0;
                if (!isClosedWithLetter) return true;
                const aid = String(att._id || att.id || '');
                const isSignedLetter = closureSet.has(aid);
                return showDraftHistory ? !isSignedLetter : isSignedLetter;
              }).map((attachment) => {
                const extractedText = attachment.extractedContent || attachment.ocrText;
                const preview = extractedText ? extractedText.substring(0, 200) + (extractedText.length > 200 ? '...' : '') : null;

                const isPlaceholder = attachment.filename === 'Attachment (loading failed)';

                return (
                  <div
                    key={attachment._id || attachment.id}
                    className={`rounded-lg border overflow-hidden ${
                      isPlaceholder
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700'
                        : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {/* Attachment header */}
                    <div className="p-3 flex items-start justify-between gap-3">
                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                        <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isPlaceholder ? 'text-amber-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`text-sm font-medium truncate ${isPlaceholder ? 'text-amber-900 dark:text-amber-100' : 'text-gray-900 dark:text-gray-100'}`}>
                              {attachment.filename || attachment.originalName}
                            </p>
                            {!isPlaceholder && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                {formatFileSize(attachment.size)}
                              </span>
                            )}
                          </div>
                          {isPlaceholder ? (
                            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                              ⚠️ Attachment uploaded but details failed to load. Save the response to persist the attachment.
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {attachment.contentType}
                              {attachment.ocrApplied && <span className="ml-2 text-blue-600 dark:text-blue-400">• OCR Applied</span>}
                              {attachment.extractedContent && <span className="ml-2 text-green-600 dark:text-green-400">• Text Extracted</span>}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!isPlaceholder && (attachment.extractedContent || attachment.ocrText) && (
                          <button
                            type="button"
                            onClick={() => handleViewOcr(attachment)}
                            className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 p-1 rounded"
                            title="View OCR Text"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </button>
                        )}
                        {!isPlaceholder && canPreview(attachment) && (
                          <button
                            type="button"
                            onClick={() => handlePreviewAttachment(attachment)}
                            className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 p-1 rounded"
                            title="Preview"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                        )}
                        {!isPlaceholder && (
                          <button
                            type="button"
                            onClick={() => handleDownloadAttachment(attachment)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 p-1 rounded"
                            title="Download"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </button>
                        )}
                        {!isPlaceholder && isEditing && (
                          <button
                            type="button"
                            onClick={() => handleReplaceAttachment(attachment)}
                            className="text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300 p-1 rounded"
                            title="Replace with new version"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        )}
                        {!isPlaceholder && (
                          <button
                            type="button"
                            onClick={() => handleViewVersionHistory(attachment)}
                            className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 p-1 rounded"
                            title="Version History"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )}
                        {isEditing && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(attachment._id || attachment.id || '')}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1 rounded"
                            disabled={isSaving || uploadingFiles}
                            title="Remove"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Extracted content preview */}
                    {preview && (
                      <div className="px-3 pb-3 pt-0">
                        <div className="bg-white dark:bg-gray-800 rounded p-2 border border-gray-200 dark:border-gray-700">
                          <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">
                            Extracted Content Preview:
                          </p>
                          <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                            {preview}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}

          {/* Upload area */}
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <div className="mt-2">
                <button
                  type="button"
                  onClick={handleAddAttachmentsClick}
                  disabled={!isEditing || uploadingFiles}
                  className={`inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md ${
                    isEditing && !uploadingFiles
                      ? 'text-blue-700 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800'
                      : 'text-gray-400 bg-gray-100 cursor-not-allowed dark:bg-gray-700'
                  }`}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {uploadingFiles ? 'Uploading...' : 'Add Attachments'}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {isEditing ? 'Upload files to include with this response' : 'Switch to edit mode to add attachments'}
              </p>
            </div>
          </div>
        </div>

        {/* Status Info */}
        <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center space-x-4">
              <span>
                Status:{' '}
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    response.status?.toString() === 'Draft'
                      ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      : response.status?.toString() === 'Approved'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : response.status?.toString() === 'Rejected'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      : response.status?.toString() === 'Sent'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }`}
                >
                  {response.status}
                </span>
              </span>
              {response.currentVersion && (
                <span>Version: {response.currentVersion}</span>
              )}
              {response.currentStage && (
                <span>Stage: {response.currentStage}</span>
              )}
            </div>

            {!canEdit && (
              <span className="text-amber-600 dark:text-amber-400">
                <svg
                  className="w-4 h-4 inline mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Read-only
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Auto-save indicator */}
      {hasChanges && isEditing && (
        <div className="px-4 pb-4">
          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-3 py-2">
            <svg
              className="w-4 h-4 mr-2 text-amber-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            You have unsaved changes. Click "Save Draft" to save your work.
          </div>
        </div>
      )}

      {/* OCR Modal */}
      {showOcrModal && selectedAttachmentForOcr && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowOcrModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Extracted Content
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {selectedAttachmentForOcr.filename}
                </p>
              </div>
              <button
                onClick={() => setShowOcrModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono">
                  {selectedAttachmentForOcr.extractedContent || selectedAttachmentForOcr.ocrText || 'No extracted content available'}
                </pre>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  const text = selectedAttachmentForOcr.extractedContent || selectedAttachmentForOcr.ocrText || '';
                  navigator.clipboard.writeText(text);
                  addNotification({ type: 'success', title: 'Copied', message: 'Text copied to clipboard.' });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Copy to Clipboard
              </button>
              <button
                onClick={() => setShowOcrModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedAttachmentForPreview && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowPreviewModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  File Preview
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {selectedAttachmentForPreview.filename}
                </p>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {loadingPreview ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : previewContent ? (
                <>
                  {previewContent.type === 'csv' && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                          <tr>
                            {previewContent.data[0]?.map((header: string, i: number) => (
                              <th key={i} className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {previewContent.data.slice(1, 101).map((row: string[], i: number) => (
                            <tr key={i}>
                              {row.map((cell: string, j: number) => (
                                <td key={j} className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {previewContent.data.length > 101 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 text-center">
                          Showing first 100 rows of {previewContent.data.length - 1} total rows
                        </p>
                      )}
                    </div>
                  )}

                  {previewContent.type === 'text' && (
                    <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono bg-gray-50 dark:bg-gray-900 p-4 rounded">
                      {previewContent.data}
                    </pre>
                  )}

                  {previewContent.type === 'pdf' && (
                    <div className="w-full h-[600px]">
                      <iframe
                        src={previewContent.url}
                        className="w-full h-full border border-gray-200 dark:border-gray-700 rounded"
                        title="PDF Preview"
                        allow="fullscreen"
                      />
                    </div>
                  )}

                  {previewContent.type === 'image' && (
                    <div className="flex justify-center">
                      <img
                        src={previewContent.url}
                        alt="Preview"
                        className="max-w-full h-auto rounded border border-gray-200 dark:border-gray-700"
                      />
                    </div>
                  )}

                  {(previewContent.type === 'excel' || previewContent.type === 'unsupported' || previewContent.type === 'error') && (
                    <div className="text-center py-12">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="mt-4 text-gray-600 dark:text-gray-400">{previewContent.message}</p>
                      {previewContent.url && (
                        <a
                          href={previewContent.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          Download File
                        </a>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No preview available
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Release Modal */}
      {showReleaseModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowReleaseModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Request Lock Release
              </h3>
              <button
                onClick={() => setShowReleaseModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded"
                disabled={requestingRelease}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Request <span className="font-medium text-gray-900 dark:text-gray-100">{getLockedByName()}</span> to release the draft lock so you can edit this response.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason (Optional)
                </label>
                <textarea
                  value={releaseReason}
                  onChange={(e) => setReleaseReason(e.target.value)}
                  placeholder="Provide a reason for requesting early release..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-none"
                  disabled={requestingRelease}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowReleaseModal(false);
                  setReleaseReason('');
                }}
                disabled={requestingRelease}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestRelease}
                disabled={requestingRelease}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {requestingRelease ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Request'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace Attachment Modal */}
      {showReplaceModal && selectedAttachmentForReplace && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Replace Attachment
              </h3>
              <button
                onClick={handleCloseReplaceModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded"
                disabled={replacingAttachment}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Current file:</strong> {selectedAttachmentForReplace.filename}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  This will create a new version while keeping the old version in history.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select new file
                </label>
                <input
                  ref={replaceFileInputRef}
                  type="file"
                  onChange={handleReplaceFileSelect}
                  disabled={replacingAttachment}
                  className="block w-full text-sm text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 focus:outline-none"
                />
                {replacementFile && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                    Selected: {replacementFile.name} ({(replacementFile.size / 1024).toFixed(2)} KB)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Change comment (optional)
                </label>
                <textarea
                  value={replaceComment}
                  onChange={(e) => setReplaceComment(e.target.value)}
                  disabled={replacingAttachment}
                  placeholder="Describe what changed in this version..."
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {replaceComment.length}/500 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Name (optional)
                </label>
                <input
                  type="text"
                  value={replaceDisplayName}
                  onChange={(e) => setReplaceDisplayName(e.target.value)}
                  disabled={replacingAttachment}
                  placeholder="Friendly name for this attachment..."
                  maxLength={255}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {replaceDisplayName.length}/255 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={replaceDescription}
                  onChange={(e) => setReplaceDescription(e.target.value)}
                  disabled={replacingAttachment}
                  placeholder="Additional details about this attachment..."
                  rows={2}
                  maxLength={1000}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {replaceDescription.length}/1000 characters
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={handleCloseReplaceModal}
                disabled={replacingAttachment}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReplacement}
                disabled={!replacementFile || replacingAttachment}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {replacingAttachment ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Replacing...
                  </>
                ) : (
                  'Replace Attachment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {showVersionHistoryModal && selectedAttachmentForHistory && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Version History
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {selectedAttachmentForHistory.filename}
                </p>
              </div>
              <button
                onClick={() => setShowVersionHistoryModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {loadingVersionHistory ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : versionHistory && versionHistory.versions && versionHistory.versions.length > 0 ? (
                <div className="space-y-4">
                  {/* Info Banner for Response Attachments */}
                  {selectedAttachmentForHistory.responseId && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="text-sm text-blue-800 dark:text-blue-200">
                          <p className="font-medium">Version History with Workflow Context</p>
                          <p className="mt-1 text-xs">
                            Each version shows the workflow stage and status when it was created, helping you track changes through the approval process.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {versionHistory.versions.map((version: any) => (
                    <div
                      key={version.versionNumber}
                      className={`border rounded-lg p-4 ${
                        version.versionNumber === versionHistory.currentVersion
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                              Version {version.versionNumber}
                            </h4>
                            {version.versionNumber === versionHistory.currentVersion && (
                              <span className="px-2 py-1 text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded">
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                            {version.filename}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {version.contentType} • {formatFileSize(version.size)}
                          </p>

                          {/* Workflow Context (for response attachments) */}
                          {(version.workflowStage || version.responseStatus) && (
                            <div className="flex items-center gap-2 mt-2">
                              {version.workflowStage && (
                                <span className={`px-2 py-1 text-xs font-medium rounded ${
                                  version.workflowStage === 'draft' ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' :
                                  version.workflowStage === 'submitted' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' :
                                  version.workflowStage === 'review' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300' :
                                  version.workflowStage === 'approved' ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' :
                                  version.workflowStage === 'rejected' ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300' :
                                  'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                }`}>
                                  Stage: {version.workflowStage.charAt(0).toUpperCase() + version.workflowStage.slice(1)}
                                </span>
                              )}
                              {version.responseStatus && (
                                <span className="px-2 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded">
                                  Status: {version.responseStatus}
                                </span>
                              )}
                            </div>
                          )}

                          {version.changeComment && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic">
                              "{version.changeComment}"
                            </p>
                          )}
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Updated by {version.updatedBy?.name || 'Unknown'} on{' '}
                            {new Date(version.updatedAt).toLocaleString()}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownloadVersion(selectedAttachmentForHistory._id!, version.versionNumber)}
                          className="ml-4 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 p-2 rounded"
                          title="Download this version"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">No version history available</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowVersionHistoryModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
